/**
 * Entry-link parameters survive only if they are read wherever the link buries them.
 * A login link wraps the destination in `returnUrl`, and the e-consent hop re-wraps it
 * (`/e-consent?returnUrl=…`), so a reader that asks only the top-level query sees nothing
 * on those pages (NP-547). The depth is bounded so a hand-crafted URL cannot make the
 * walk run forever.
 */
const MAX_RETURN_URL_DEPTH = 3;

/**
 * The first level of `search` — itself, then each nested `returnUrl` — for which `read`
 * returns a value.
 */
export function searchThroughReturnUrl<T>(
  search: string | null | undefined,
  read: (params: URLSearchParams) => T | null,
): T | null {
  return searchAt(search, read, 0);
}

/** Depth is private: exported, a caller could pass a negative one and unbound the walk. */
function searchAt<T>(
  search: string | null | undefined,
  read: (params: URLSearchParams) => T | null,
  depth: number,
): T | null {
  if (!search) return null;
  const params = new URLSearchParams(search);

  const direct = read(params);
  if (direct !== null) return direct;
  if (depth >= MAX_RETURN_URL_DEPTH) return null;

  // URLSearchParams has already percent-decoded the value, so a wrapped link
  // arrives here as e.g. "/?lo=someone@afncorp.com".
  const returnUrl = params.get('returnUrl');
  if (!returnUrl || !returnUrl.includes('?')) return null;

  return searchAt(returnUrl.slice(returnUrl.indexOf('?')), read, depth + 1);
}
